import React, { useMemo, useState } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

type ChartTooltipProps = {
  metrics: {
    key: number | string;
    primaryNumber: string;
    secondaryText: string;
  }[];
  color: string;
  getLinkToDeepDive: (
    treatmentDimensionLevel: string,
  ) => Promise<string | undefined>;
  level: string;
  comparisonGroupValue: string;
  whiteLabelingSubjectPlural: string;
};

export const ChartTooltip = ({
  metrics,
  color,
  getLinkToDeepDive,
  level,
  comparisonGroupValue,
  whiteLabelingSubjectPlural,
}: ChartTooltipProps) => {
  const language = useLanguage();

  const [linkToDeepDive, setLinkToDeepDive] = useState<string | undefined>(
    undefined,
  );

  useAsyncEffect(async () => {
    const linkToDeepDive = await getLinkToDeepDive(level);
    setLinkToDeepDive(linkToDeepDive);
  }, [getLinkToDeepDive, level]);

  const learnMoreButton = useMemo(() => {
    if (linkToDeepDive) {
      return (
        <TouchTarget
          content={() => {
            return (
              <div className="flex items-center h-6 ">
                <S2Typography
                  underline
                  variant={'link'}
                  translatedContent={language.learnMore()}
                  textColorVariant={'royal-700'}
                />
              </div>
            );
          }}
          behavior={{
            type: 'href',
            href: linkToDeepDive,
          }}
        />
      );
    }
    return (
      <div className="flex items-center h-6 ">
        <Spinner size={16} />
      </div>
    );
  }, [language, linkToDeepDive]);

  const title =
    level === comparisonGroupValue
      ? language.chartTooltipTitleSingleGroup({
          level,
          whiteLabelingSubjectPlural,
        })
      : language.chartTooltipTitleComparisonGroup({
          level,
          comparisonGroupValue,
          whiteLabelingSubjectPlural,
        });

  return (
    <div
      className="inline-block text-sm w-full"
      data-testid="progress-chart-tooltip"
    >
      <Box display="flex" flexDirection="col">
        <Spacing size="sm" direction="vertical">
          <span className="font-bold">{title}</span>

          <Box display="flex" flexDirection="col">
            <Spacing size="sm" direction="vertical">
              {metrics.map(({ key, primaryNumber, secondaryText }) => (
                <div key={key} data-testid={key}>
                  <span className="font-bold">{primaryNumber}</span>{' '}
                  <span className="text-gray-600">{secondaryText}</span>
                </div>
              ))}

              {level !== comparisonGroupValue && learnMoreButton}
            </Spacing>
          </Box>
        </Spacing>
      </Box>
    </div>
  );
};
